import styled from "@emotion/styled";
import React, { useMemo } from "react";

import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import { BynderAsset } from "@brexhq/next/src/components/shared/BynderAsset/BynderAsset";
import type { IBynderAssetWithBorderRadius } from "@brexhq/shared/types/BynderAsset";
import { TextOverImageSpan } from "@brexhq/shared/types/modules/BlockRowsModule/BlockRowItems/TextOverImage";

interface Props {
  contentAlignX: "left" | "center" | "right";
  contentAlignY: "flex-start" | "center" | "flex-end";
  media: IBynderAssetWithBorderRadius;
  imageSpan: TextOverImageSpan;
  height: string;
  maxWidth: number;
  isDesktop?: boolean;
  imageAlignment?: string | null;
}

const Media = styled(BynderAsset)<{
  borderRadius?: string | number;
  imageAlignment?: string | null;
}>(({ borderRadius, imageAlignment }) => ({
  borderRadius,
  position: "absolute",
  height: "100%",
  width: "100%",
  objectFit: "cover",
  ...(imageAlignment && {
    objectPosition: imageAlignment,
  }),
}));

const getBorderRadius = (
  mediaStyle: IBynderAssetWithBorderRadius["mediaStyle"],
): string | number | undefined => {
  if (mediaStyle.borderRadius === "Custom") {
    return mediaStyle.customBorderRadius ?? undefined;
  }
  return mediaStyle.borderRadius;
};

export const ScrimMedia = ({
  contentAlignX,
  contentAlignY,
  media,
  imageSpan,
  height,
  maxWidth,
  isDesktop,
  imageAlignment,
}: Props) => {
  const borderRadius = getBorderRadius(media.mediaStyle);

  const background = useMemo(() => {
    let direction: string;
    switch (contentAlignX) {
      case "left": {
        switch (contentAlignY) {
          case "flex-start":
            direction = "to bottom right";
            break;
          case "center":
            direction = "to right";
            break;
          case "flex-end":
            direction = "to top right";
        }
        break;
      }
      case "center": {
        switch (contentAlignY) {
          case "flex-start":
            direction = "to bottom";
            break;
          case "center":
            return "radial-gradient(55% 55% at 50% 50%, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0) 96%)";
          case "flex-end":
            direction = "to top";
        }
        break;
      }
      case "right": {
        switch (contentAlignY) {
          case "flex-start":
            direction = "to bottom left";
            break;
          case "center":
            direction = "to left";
            break;
          case "flex-end":
            direction = "to top left";
        }
        break;
      }
    }
    return `linear-gradient(${direction}, rgba(0, 0, 0, 0.7) 19%, rgba(0, 0, 0, 0) 52%)`;
  }, [contentAlignX, contentAlignY]);

  return (
    <Container
      width={"100%"}
      position={imageSpan === "full-width" ? "absolute" : undefined}
      height={height}
      display={{
        LRG: isDesktop ? "block" : "none",
        MED: isDesktop ? "block" : "none",
        SML: isDesktop ? "none" : "block",
      }}
    >
      <Media
        imageAlignment={imageAlignment}
        borderRadius={borderRadius}
        asset={media.media}
        thumbnail={media.thumbnail}
        maxWidth={maxWidth}
      />
      <div
        style={{
          height: "100%",
          width: "100%",
          position: "absolute",
          inset: 0,
          background,
          borderRadius: borderRadius,
        }}
      />
    </Container>
  );
};
